import type { PriceBreakdown as Breakdown } from "@/lib/pricing-breakdown";

interface PriceBreakdownProps {
  breakdown: Breakdown;
  variant?: "form" | "confirmation";
}

/**
 * Renders every rental line from the breakdown (including 0 € lines) plus the
 * total. The refundable deposit is a line item above the total (it is charged
 * up front together with the rental and returned by bank transfer at the end),
 * so the deposit is part of `breakdown.total`.
 * Used live inside BookingForm and post-submit inside BookingConfirmation.
 */
export default function PriceBreakdown({ breakdown, variant = "form" }: PriceBreakdownProps) {
  const borderColor = variant === "confirmation" ? "border-green-200" : "border-blue-200";
  const rowText = variant === "confirmation" ? "text-green-900" : "text-blue-900";
  const mutedText = variant === "confirmation" ? "text-green-700" : "text-blue-700";

  return (
    <div className={`space-y-1 text-sm ${rowText}`}>
      <div className="flex justify-between">
        <span className={mutedText}>{breakdown.base.label}</span>
        <span className="font-medium">{breakdown.base.amount} €</span>
      </div>

      <div className="flex justify-between">
        <span className={mutedText}>Atsiėmimas: {breakdown.pickup_location.name}</span>
        <span className="font-medium">{breakdown.pickup_location.surcharge} €</span>
      </div>

      <div className="flex justify-between">
        <span className={mutedText}>Grąžinimas: {breakdown.return_location.name}</span>
        <span className="font-medium">{breakdown.return_location.surcharge} €</span>
      </div>

      {breakdown.extras.map((e, i) => (
        <div key={i} className="flex justify-between">
          <span className={mutedText}>{e.name}</span>
          <span className="font-medium">{e.price} €</span>
        </div>
      ))}

      {breakdown.deposit > 0 && (
        <div className="flex justify-between">
          <span className={mutedText}>Užstatas (grąžinamas)</span>
          <span className="font-medium">{breakdown.deposit} €</span>
        </div>
      )}

      <div className={`flex justify-between border-t ${borderColor} pt-2 mt-2`}>
        <span className="font-semibold">Iš viso</span>
        <span className="text-lg font-bold">{breakdown.total} €</span>
      </div>
    </div>
  );
}
